/**
 * BN product page delivery quote (Empire theme).
 *
 * Markup: snippets/bn-pdp-delivery.liquid. Response checks:
 * delivery-quote-core.js (window.ETDeliveryCore, shared with the ET theme).
 *
 * Quotes come from an independent Shopify calculation on a temporary cart,
 * never the shopper's basket cookie or a temporary add/remove. No checkout URL,
 * analytics event or storage. Ported from ET's etr-pdp-delivery.js; what
 * differs is Empire's variant/quantity hooks and the detached delivery form.
 */
(() => {
  'use strict';
  const CREATE = `mutation BNDeliveryQuote($input: CartInput!) {
    cartCreate(input: $input) {
      cart { id totalQuantity lines(first: 250) { nodes {
        quantity attributes { key value }
        merchandise { ... on ProductVariant { id } }
        sellingPlanAllocation { sellingPlan { id } }
      } pageInfo { hasNextPage } } }
      userErrors { message } warnings { code message }
    }
  }`;
  const RATES = `query BNDeliveryQuoteRates($id: ID!) {
    cart(id: $id) { totalQuantity ...BNCarrierRates @defer }
  }
  fragment BNCarrierRates on Cart {
    deliveryGroups(first: 10, withCarrierRates: true) {
      nodes { groupType deliveryOptions {
        title code deliveryMethodType estimatedCost { amount currencyCode }
      } }
      pageInfo { hasNextPage }
    }
  }`;
  const STATES = new Set(['ACT', 'NSW', 'NT', 'QLD', 'SA', 'TAS', 'VIC', 'WA']);
  // Grams. Parcel services stop at 20 kg; heavier baskets need a freight quote
  // (shipping policy, "Heavy Orders & Carrier Weight Limits").
  const MAX_WEIGHT = 20000;

  function boot() {
    document.querySelectorAll('[data-bn-delivery]').forEach(bind);
  }

  function bind(panel) {
    const core = window.ETDeliveryCore;
    if (panel.dataset.bound || !core) return;
    const productForm = panel.closest('form');
    const form = document.getElementById(panel.dataset.deliveryForm);
    const fields = panel.querySelector('[data-bn-delivery-fields]');
    const status = panel.querySelector('[data-bn-delivery-status]');
    const results = panel.querySelector('[data-bn-delivery-results]');
    if (!productForm || !form || !fields || !status || !results) return;
    let variants;
    try { variants = JSON.parse(panel.querySelector('[data-bn-delivery-variants]').textContent); }
    catch { return; }
    const postcode = form.elements.postcode;
    const province = form.elements.province;
    const button = fields.querySelector('button[type="submit"]');
    if (!postcode || !province || !button) return;
    const disclosure = panel.querySelector('[data-bn-delivery-disclosure]');
    const title = panel.querySelector('[data-bn-delivery-title]');
    const intro = panel.querySelector('[data-bn-delivery-intro]');
    const summary = panel.querySelector('[data-bn-delivery-summary]');
    const action = panel.querySelector('[data-bn-delivery-action]');
    let hasSummary = false;
    let controller;
    let sequence = 0;
    let stamp = '';
    panel.dataset.bound = 'true';
    fields.hidden = false;

    // Keep native disclosure keyboard behaviour; never move focus after a quote.
    function updateAction() {
      if (action) action.textContent = disclosure && disclosure.open ? panel.dataset.close :
        (hasSummary ? panel.dataset.change : panel.dataset.check);
    }
    function clearSummary() {
      hasSummary = false;
      if (title) title.textContent = panel.dataset.heading;
      if (intro) intro.hidden = false;
      if (summary) { summary.replaceChildren(); summary.hidden = true; }
      updateAction();
    }
    function showSummary(groups, zip, state, money) {
      if (!title || !summary) return;
      title.textContent = panel.dataset.summaryDestination.replace('__POSTCODE__', zip).replace('__STATE__', state);
      if (groups.length === 1) {
        const options = groups[0].options;
        const cheapest = options.reduce((lowest, option) => option.cents < lowest.cents ? option : lowest);
        const price = (options.length > 1 ? panel.dataset.summaryFrom : '') + money.format(cheapest.cents / 100);
        summary.textContent = panel.dataset.summaryRate.replace('__TITLE__', cheapest.title).replace('__PRICE__', price);
      } else {
        // Never combine separate shipments or imply one group's cost covers the basket.
        summary.textContent = panel.dataset.summaryMultiple;
      }
      if (intro) intro.hidden = true;
      summary.hidden = false;
      hasSummary = true;
      updateAction();
    }
    if (disclosure) disclosure.addEventListener('toggle', updateAction);
    updateAction();

    // Empire records the chosen variant on <variant-selection variant="…"> first,
    // then mirrors it into the form's id field once the product JSON arrives.
    function selection() {
      const picker = productForm.querySelector('[data-variant-selection]');
      const idField = productForm.querySelector('[name="id"]');
      const id = String((picker && picker.getAttribute('variant')) || (idField && idField.value) || '');
      const variant = variants.find(item => String(item.id) === id);
      const quantityField = productForm.querySelector('[name="quantity"]');
      const quantity = Number(quantityField ? quantityField.value : 1);
      const plan = new FormData(productForm).get('selling_plan');
      return { variant, variantId: id, quantity, sellingPlanId: plan ? String(plan) : '' };
    }
    function signature() {
      const chosen = selection();
      return JSON.stringify([chosen.variantId, chosen.quantity, chosen.sellingPlanId]);
    }
    function cancel(stale = false) {
      const hadQuote = results.childElementCount || status.textContent;
      sequence += 1;
      if (controller) controller.abort();
      controller = null;
      results.replaceChildren();
      results.hidden = true;
      clearSummary();
      status.textContent = stale && hadQuote ? panel.dataset.stale : '';
      panel.setAttribute('aria-busy', 'false');
      button.disabled = false;
    }
    function changed() {
      const next = signature();
      if (next !== stamp) {
        cancel(true);
        stamp = next;
      }
      const { variant } = selection();
      panel.hidden = !variant || !variant.available || !variant.requiresShipping;
    }
    async function request(query, variables, operationName, signal) {
      const response = await fetch('/api/2026-10/graphql.json', {
        method: 'POST', credentials: 'omit', cache: 'no-store', signal,
        headers: { 'Content-Type': 'application/json', Accept: 'application/json, multipart/mixed' },
        body: JSON.stringify({ query, variables, operationName }),
      });
      if (!response.ok) throw new Error('Delivery calculation unavailable');
      return core.parseQuoteResponse(await response.text(), response.headers.get('content-type') || '');
    }

    form.addEventListener('submit', async event => {
      event.preventDefault();
      if (!form.reportValidity() || !STATES.has(province.value)) return;
      const chosen = selection();
      const quantityField = productForm.querySelector('[name="quantity"]');
      if (!chosen.variant || !chosen.variant.available || !chosen.variant.requiresShipping ||
          !Number.isSafeInteger(chosen.quantity) || chosen.quantity < 1 ||
          (quantityField && !quantityField.reportValidity())) return;
      cancel();
      const current = ++sequence;
      const selectedStamp = signature();
      const zip = postcode.value.trim();
      const state = province.value;
      controller = new AbortController();
      const active = controller;
      const timeout = setTimeout(() => active.abort(), 25000);
      button.disabled = true;
      panel.setAttribute('aria-busy', 'true');
      status.textContent = panel.dataset.loading;
      try {
        if (panel.dataset.currency !== 'AUD') throw new Error('Unsupported currency');
        const cartResponse = await fetch(panel.dataset.cartUrl + '.js', {
          credentials: 'same-origin', cache: 'no-store', signal: active.signal,
        });
        if (!cartResponse.ok) throw new Error('Cart unavailable');
        const cart = await cartResponse.json();
        if (cart.currency !== 'AUD') throw new Error('Unsupported currency');
        if (!Number.isFinite(cart.total_weight) || !Number.isFinite(chosen.variant.weight)) {
          throw new Error('Unverified weight');
        }
        if (cart.total_weight + chosen.variant.weight * chosen.quantity > MAX_WEIGHT) {
          status.textContent = panel.dataset.heavy;
          return;
        }
        if (chosen.sellingPlanId || cart.items.some(item => item.selling_plan_allocation)) {
          status.textContent = panel.dataset.subscription;
          return;
        }
        const lines = core.buildLines(cart, chosen);
        const created = await request(CREATE, { input: {
          lines, buyerIdentity: { countryCode: 'AU' },
          delivery: { addresses: [{ selected: true, oneTimeUse: true, validationStrategy: 'COUNTRY_CODE_ONLY',
            address: { deliveryAddress: { countryCode: 'AU', provinceCode: state, zip } } }] },
        } }, 'BNDeliveryQuote', active.signal);
        const quote = core.verifyQuoteCart(created, lines);
        const payload = await request(RATES, { id: quote.id }, 'BNDeliveryQuoteRates', active.signal);
        const groups = core.verifiedGroups(payload);
        if (current !== sequence || active.signal.aborted || signature() !== selectedStamp) return;
        if (!groups.length || groups.some(group => !group.options.length)) {
          status.textContent = panel.dataset.empty;
          return;
        }
        // en-AU prints "$20.00", as the theme does; a bare "en" would print "A$20.00".
        const money = new Intl.NumberFormat('en-AU', { style: 'currency', currency: 'AUD' });
        for (const [index, group] of groups.entries()) {
          if (groups.length > 1) {
            const heading = document.createElement('p');
            heading.className = 'bn-delivery__group';
            heading.textContent = panel.dataset.group.replace('__COUNT__', String(index + 1));
            results.append(heading);
          }
          const list = document.createElement('ul');
          list.className = 'bn-delivery__rates';
          for (const option of group.options) {
            const row = document.createElement('li');
            const label = document.createElement('span');
            const price = document.createElement('strong');
            label.textContent = option.title;
            price.textContent = money.format(option.cents / 100);
            row.append(label, price);
            list.append(row);
          }
          results.append(list);
        }
        results.hidden = false;
        status.textContent = panel.dataset.destination.replace('__POSTCODE__', zip);
        showSummary(groups, zip, state, money);
      } catch {
        if (current === sequence) {
          results.replaceChildren();
          results.hidden = true;
          status.textContent = panel.dataset.error;
        }
      } finally {
        clearTimeout(timeout);
        // An older finalizer must never release the controller owned by a newer,
        // still-active request.
        if (controller === active) controller = null;
        if (current === sequence) {
          button.disabled = false;
          panel.setAttribute('aria-busy', 'false');
        }
      }
    });
    for (const field of [postcode, province]) {
      field.addEventListener('input', () => cancel(true));
      field.addEventListener('change', () => cancel(true));
    }

    // The delivery fields sit inside the product form's markup but belong to
    // their own form, so skip their events here.
    const productChange = event => {
      if (event.target && event.target.form && event.target.form !== productForm) return;
      changed();
      requestAnimationFrame(changed);
    };
    for (const name of ['input', 'change']) productForm.addEventListener(name, productChange);
    // Empire's +/- buttons set the quantity without an event.
    productForm.addEventListener('click', event => {
      if (!panel.contains(event.target)) requestAnimationFrame(changed);
    });
    // Empire dispatches a non-bubbling "variant-change" on <variant-selection>.
    document.addEventListener('variant-change', event => {
      if (productForm.contains(event.target)) requestAnimationFrame(changed);
    }, true);
    // BN's cart drawer fires cart:updated; Empire fires cartcount:update after an add.
    document.addEventListener('cart:updated', () => cancel(true));
    window.addEventListener('cartcount:update', () => cancel(true));
    stamp = signature();
    changed();
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', boot, { once: true });
  else boot();
  document.addEventListener('shopify:section:load', boot);
})();
